import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Title, Paper, Stack, Group, Text, Badge, Loader, Center, ActionIcon, Avatar, Menu, TextInput, Modal, Select, Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconUser, IconUsers as IconGroup, IconDots, IconEdit, IconSearch, IconPlus, IconUsersGroup, IconUserPlus, IconTrash } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useProfileTypes } from '../../hooks/useProfileTypes';
import { profileTypesApi, usersApi, userProfilesApi, groupsApi, groupProfilesApi } from '../../api/client';
import { PageContainer } from '../../components/PageContainer';
import { PaginatedList } from '../../components/List';
import { Group as GroupType } from '../../types/group';
import { User } from '../../types/user';

interface ProfileUser {
  id: string;
  login: string;
  first_name?: string;
  last_name?: string;
  has_direct: boolean;
  sources?: string[];
}

interface UserCardProps {
  user: ProfileUser;
  onClick: () => void;
  onUnlink?: () => void;
}

function UserCard({ user, onClick, onUnlink }: UserCardProps) {
  const handleCardClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-menu-trigger]') || 
        (e.target as HTMLElement).closest('.mantine-Menu-dropdown')) {
      return;
    }
    onClick();
  };

  return (
    <Paper 
      p="md" 
      withBorder 
      style={{ cursor: 'pointer', transition: 'box-shadow 0.2s' }}
      onClick={handleCardClick}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--mantine-shadow-sm)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ''; }}
    >
      <Group justify="space-between" mb="xs">
        <Group gap="sm">
          <Avatar size="sm" radius="xl">
            <IconUser size={16} />
          </Avatar>
          <div>
            <Text fw={600} style={{ fontSize: 'var(--font-size-sm)' }}>
              {user.first_name} {user.last_name}
            </Text>
            <Text c="dimmed" style={{ fontSize: 'var(--font-size-xs)' }}>{user.login}</Text>
          </div>
        </Group>
        <Menu shadow="md" width={200} position="bottom-end">
          <Menu.Target>
            <ActionIcon variant="subtle" size="sm" data-menu-trigger onClick={(e) => e.stopPropagation()}>
              <IconDots size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item leftSection={<IconEdit size={14} />} onClick={onClick}>Редактировать профиль</Menu.Item>
            {user.has_direct && onUnlink && (
              <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={onUnlink}>
                Отвязать профиль
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>
      </Group>
      
      <Group gap="xs" mb="xs">
        <Badge 
          variant="light" 
          color={user.has_direct ? 'green' : 'blue'}
          style={{ fontSize: 'var(--font-size-xs)' }}
        >
          {user.has_direct ? 'Прямой' : 'Через группу'}
        </Badge>
      </Group>
      
      <Group gap={4} wrap="wrap">
        {user.sources?.slice(0, 3).map((source: string, i: number) => (
          <Badge key={i} variant="outline" leftSection={<IconGroup size={10} />} style={{ padding: 'var(--badge-padding)', fontSize: 'var(--font-size-xs)' }}>
            {source}
          </Badge>
        ))}
        {user.sources && user.sources.length > 3 && (
          <Badge variant="light" color="gray" style={{ padding: 'var(--badge-padding)', fontSize: 'var(--font-size-xs)' }}>
            +{user.sources.length - 3}
          </Badge>
        )}
      </Group>
    </Paper>
  );
}

export function ProfileUsersPage() {
  const { profileType } = useParams<{ profileType: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [linkUserModal, setLinkUserModal] = useState(false);
  const [linkGroupModal, setLinkGroupModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [linking, setLinking] = useState(false);
  
  const { profileTypes, isLoading: typesLoading } = useProfileTypes();
  const schema = profileTypes.find(pt => pt.id === profileType);

  // Load users for linking
  const { data: allUsers = [] } = useQuery<User[]>({
    queryKey: ['users-for-link'],
    queryFn: async () => {
      const res = await usersApi.list({ limit: 100 });
      return res.data?.data?.data || res.data?.data || [];
    },
    enabled: linkUserModal,
  });

  // Load groups for linking
  const { data: groups = [] } = useQuery<GroupType[]>({
    queryKey: ['groups'],
    queryFn: async () => {
      const res = await groupsApi.list();
      return res.data?.data || [];
    },
    enabled: linkGroupModal,
  });

  const refreshUsers = () => {
    queryClient.invalidateQueries({ queryKey: ['profile-users', profileType] });
  };

  const submitLinkUser = async () => {
    if (!profileType || !selectedUser) return;
    setLinking(true);
    try {
      await userProfilesApi.set(selectedUser, profileType);
      notifications.show({ title: 'Успех', message: 'Профиль привязан к пользователю', color: 'green' });
      refreshUsers();
      setLinkUserModal(false);
      setSelectedUser(null);
    } catch {
      notifications.show({ title: 'Ошибка', message: 'Не удалось привязать профиль', color: 'red' });
    } finally {
      setLinking(false);
    }
  };

  const submitLinkGroup = async () => {
    if (!profileType || !selectedGroup) return;
    setLinking(true);
    try {
      await groupProfilesApi.link(selectedGroup, profileType);
      notifications.show({ title: 'Успех', message: 'Профиль привязан к группе', color: 'green' });
      refreshUsers();
      setLinkGroupModal(false);
      setSelectedGroup(null);
    } catch {
      notifications.show({ title: 'Ошибка', message: 'Не удалось привязать профиль', color: 'red' });
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkUser = async (userId: string) => {
    if (!profileType) return;
    try {
      await userProfilesApi.delete(userId, profileType);
      notifications.show({ title: 'Успех', message: 'Профиль отвязан', color: 'green' });
      refreshUsers();
    } catch {
      notifications.show({ title: 'Ошибка', message: 'Не удалось отвязать профиль', color: 'red' });
    }
  };

  if (typesLoading) {
    return <Center py="xl"><Loader size="lg" /></Center>;
  }

  if (!schema) {
    return (
      <Center py="xl">
        <Text c="dimmed">Тип профиля не найден</Text>
      </Center>
    );
  }

  return (
    <PageContainer>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group>
            <ActionIcon variant="subtle" onClick={() => navigate(-1)}>
              <IconArrowLeft size={20} />
            </ActionIcon>
            <Title order={2}>{schema.name}</Title>
            <Badge variant="outline">{schema.plugin_id}</Badge>
          </Group>
          <Menu shadow="md" width={220}>
            <Menu.Target>
              <ActionIcon variant="filled" color="blue" size="lg">
                <IconPlus size={20} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Привязать профиль</Menu.Label>
              <Menu.Item leftSection={<IconUserPlus size={14} />} onClick={() => setLinkUserModal(true)}>
                Добавить пользователя
              </Menu.Item>
              <Menu.Item leftSection={<IconUsersGroup size={14} />} onClick={() => setLinkGroupModal(true)}>
                Добавить группу
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>

        <Text c="dimmed" style={{ fontSize: 'var(--font-size-sm)' }}>{schema.description}</Text>

        <TextInput
          placeholder="Поиск пользователей..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />

        <PaginatedList<ProfileUser>
          queryKey={['profile-users', profileType, { search }]}
          fetchFn={async (offset, limit) => {
            const res = await profileTypesApi.getUsersByType(profileType || '', { limit, offset });
            let data = (res.data?.data || []) as ProfileUser[];
            const total = res.data?.total || data.length;
            if (search) {
              const s = search.toLowerCase();
              data = data.filter(u =>
                u.login?.toLowerCase().includes(s) ||
                u.first_name?.toLowerCase().includes(s) ||
                u.last_name?.toLowerCase().includes(s)
              );
            }
            return { data, total };
          }}
          renderItem={(user) => (
            <UserCard
              user={user}
              onClick={() => navigate(`/users/${user.id}`)}
              onUnlink={() => handleUnlinkUser(user.id)}
            />
          )}
          getItemKey={(user) => user.id}
          emptyMessage="Нет пользователей с этим профилем"
          pageSize={20}
          showPageInfo
          showPageSizeSelector
        />
      </Stack>

      {/* Link User Modal */}
      <Modal opened={linkUserModal} onClose={() => setLinkUserModal(false)} title="Добавить пользователя">
        <Stack gap="md">
          <Text c="dimmed" style={{ fontSize: 'var(--font-size-sm)' }}>
            Пользователь получит профиль "{schema.name}" напрямую
          </Text>
          <Select
            label="Пользователь"
            placeholder="Выберите пользователя"
            data={allUsers.map(u => ({ value: u.id, label: `${u.login} ${u.first_name ? `(${u.first_name} ${u.last_name || ''})` : ''}`.trim() }))}
            value={selectedUser}
            onChange={setSelectedUser}
            searchable
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setLinkUserModal(false)}>Отмена</Button>
            <Button onClick={submitLinkUser} loading={linking} disabled={!selectedUser}>
              Добавить
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Link Group Modal */}
      <Modal opened={linkGroupModal} onClose={() => setLinkGroupModal(false)} title="Добавить группу">
        <Stack gap="md">
          <Text c="dimmed" style={{ fontSize: 'var(--font-size-sm)' }}>
            Все пользователи группы получат профиль "{schema.name}"
          </Text>
          <Select
            label="Группа"
            placeholder="Выберите группу"
            data={groups.map(g => ({ value: g.id, label: g.name }))}
            value={selectedGroup}
            onChange={setSelectedGroup}
            searchable
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setLinkGroupModal(false)}>Отмена</Button>
            <Button onClick={submitLinkGroup} loading={linking} disabled={!selectedGroup}>
              Добавить
            </Button>
          </Group>
        </Stack>
      </Modal>
    </PageContainer>
  );
}

export default ProfileUsersPage;
